<template>
  <app-page
    :title="(employee && employee.full_name) || $t('employee.title')"
    :subtitle="$t('employee.subtitle')"
    :error="errorEmployee"
    :previous="[{ label: $t('employee.previousLabel'), to: { name: 'employees' } }]"
    icon="user"
    :key="$route.params.employeeId"
    class="employee-page"
  >
    <template v-slot:after-header>
      <employee-actions v-if="!loadingEmployee && employee && employee.employee_id" :employee="employee" />
      <sme-alert v-if="showEmployeeFlag" :level="flagLevel" class="mt-3">{{ flagContent }}</sme-alert>
    </template>
    <template v-slot:infobox>
      <sme-info-box :title="$t('employee.infoBox.managingEmployees.title')" id="managing-employees" save-state>
        <i18n path="employee.infoBox.managingEmployees.body1" tag="p">
          <template #employeeTab
            ><strong>{{ $t('employee.tabs.employee') }}</strong></template
          >
        </i18n>

        <i18n path="employee.infoBox.managingEmployees.body2" tag="p">
          <template #edit
            ><strong>{{ $t('employee.editLabel') }}</strong></template
          >
        </i18n>
      </sme-info-box>

      <sme-info-box :title="$t('employee.infoBox.viewingShifts.title')" id="viewing-shifts" save-state>
        <i18n path="employee.infoBox.viewingShifts.body1" tag="p">
          <template #shiftsTab
            ><strong>{{ $t('employee.tabs.shifts') }}</strong></template
          >
        </i18n>
        <p>{{ $t('employee.infoBox.viewingShifts.body2') }}</p>
      </sme-info-box>

      <sme-info-box v-if="!hideSalary" :title="$t('employee.infoBox.viewingPay.title')" id="viewing-pay" save-state>
        <i18n path="employee.infoBox.viewingPay.body1" tag="p">
          <template #payTab
            ><strong>{{ $t('employee.tabs.pay') }}</strong></template
          >
        </i18n>
        <p>{{ $t('employee.infoBox.viewingPay.body2') }}</p>
      </sme-info-box>
    </template>

    <template v-if="!loadingEmployee && employee && employee.employee_id" v-slot:header>
      <div class="employee-status-chips">
        <sme-badge :variant="badgeVariant(employee)" large>{{
          $t(EMPLOYEE_STATUS_LABEL_KEYS[convertStatus(employee)])
        }}</sme-badge>
        <sme-badge v-if="employee.has_savings_account" :variant="primary" large>{{
          $t('employee.savingsAccountBadge')
        }}</sme-badge>
      </div>
    </template>

    <template v-if="!loadingEmployee" v-slot:before-content>
      <b-col sm="12" class="mb-4">
        <page-metrics :metrics="employeeMetrics" />
      </b-col>
    </template>

    <template v-if="loadingEmployee">
      <app-loading />
    </template>
    <template v-else>
      <navigable-tabs>
        <b-tab :id="TAB_NAMES.employee">
          <template #title>
            <h4>{{ $t('employee.tabs.employee') }}</h4>
          </template>
          <employee-form
            class="pt-4 pb-2 px-2"
            v-model="employee"
            :balance="balance"
            :field-permissions="fieldPermissions"
            :saving="saving"
            :state="state"
            :integrations-state="integrationsState"
            :hide-earnings="hideEarnings"
            :hide-salary="hideSalary"
            :paySchedules="paySchedules"
            :employeePayScheduleName="employeePayScheduleName"
            @save="onSave"
          ></employee-form>
        </b-tab>
        <b-tab :id="TAB_NAMES.shifts">
          <template #title>
            <h4>{{ $t('employee.tabs.shifts') }}</h4>
          </template>
          <shifts-list
            v-if="!displayNetPayShifts"
            :field-keys="shiftsListFieldKeys"
            :filter-keys="shiftFilterKeys"
            :employeeId="employee.employee_id"
            :fixed-filters="{ [SHIFT_FILTER_KEYS.PAY_SCHEDULE]: employee.pay_schedule_id }"
            :fixed-search-text="employee.full_name"
            class="pt-4 px-2"
            nested
          />
          <net-pay-shifts-list
            v-if="displayNetPayShifts"
            :field-keys="netPayShiftFieldKeys"
            :filter-keys="shiftFilterKeys"
            :employeeId="employee.employee_id"
            :fixed-filters="{ [SHIFT_FILTER_KEYS.PAY_SCHEDULE]: employee.pay_schedule_id }"
            :fixed-search-text="employee.full_name"
            class="pt-4 px-2"
            nested
          />
        </b-tab>
        <b-tab v-if="isEnableStreamedWagesPage || isEnableShowPayschedulesOnStreamedWagesPage" :id="TAB_NAMES.streams">
          <template #title>
            <h4>{{ $t('employee.tabs.payTransfers') }}</h4>
          </template>
          <StreamedWagesList
            :employeeId="employee.employee_id"
            :fieldKeys="streamFieldKeys"
            :filterKeys="streamFilterKeys"
            class="pt-4 px-2"
            nested
          />
        </b-tab>
        <b-tab :id="TAB_NAMES.absences">
          <template #title>
            <h4>{{ $t('employee.tabs.absences') }}</h4>
          </template>
          <AbsencesList
            :fixedSearchText="employee.full_name"
            :employeeId="employee.employee_id"
            :fieldKeys="absenceFieldKeys"
            class="pt-4 px-2"
            nested
          />
        </b-tab>
        <b-tab v-if="!hideEarnings && !hideSalary" :id="TAB_NAMES.pay" @shown="getPay">
          <template #title>
            <h4>{{ $t('employee.tabs.pay') }}</h4>
          </template>
          <section class="pt-4 px-2">
            <app-loading :loading="loadingPay" />
            <template v-if="!loadingPay">
              <sme-alert v-if="errorPay" :level="ALERT_LEVELS.DANGER">{{ errorPay }}</sme-alert>
              <employee-pay
                v-else
                :salaries="salaries"
                :employeeName="employee.full_name"
                :offCyclePayments="employeeOffCyclePayments"
              />
            </template>
          </section>
        </b-tab>
        <b-tab v-if="showUSAOffCyclePayments" :id="TAB_NAMES.usa_off_cycle_payments" @shown="getUSAOffCyclePayments">
          <template #title>
            <h4>{{ $t('employee.tabs.offCycleInvoices') }}</h4>
          </template>
          <section class="pt-4 px-2">
            <app-loading :loading="loadingUSAOffCyclePayments" />
            <template v-if="!loadingUSAOffCyclePayments">
              <sme-alert v-if="errorUSAOffCyclePayments" :level="ALERT_LEVELS.DANGER">{{
                errorUSAOffCyclePayments
              }}</sme-alert>
              <employee-off-cycle
                v-else
                :employeeId="employee.employee_id"
                :offCyclePayments="employeeOffCyclePayments"
              />
            </template>
          </section>
        </b-tab>
        <b-tab :id="TAB_NAMES.enrollments" @shown="getEnrollments">
          <template #title>
            <h4>{{ $t('employee.tabs.enrollments') }}</h4>
          </template>
          <section class="pt-4 px-2">
            <app-loading :loading="loadingEnrollments" />
            <template v-if="!loadingEnrollments">
              <sme-alert v-if="errorEnrollments" :level="ALERT_LEVELS.DANGER">{{ errorEnrollments }}</sme-alert>
              <employee-history v-else :history="history" />
            </template>
          </section>
        </b-tab>
        <b-tab :id="TAB_NAMES.activity_log" @shown="getActivityLog">
          <template #title>
            <h4>{{ $t('employee.tabs.activityLog') }}</h4>
          </template>
          <section class="pt-4 px-2">
            <app-loading :loading="loadingActivityLog" />
            <template v-if="!loadingActivityLog">
              <sme-alert v-if="errorActivityLog" :level="ALERT_LEVELS.DANGER">{{ errorActivityLog }}</sme-alert>
              <employee-activity-log
                v-else
                :activity="activity"
                :optout="optout"
                :salary-history="!hideSalary ? salaryHistory : []"
              />
            </template>
          </section>
        </b-tab>
        <b-tab :id="TAB_NAMES.lump_sum" v-if="showLumpSumTab" @shown="getLumpSumData">
          <template #title>
            <h4>{{ $t('employee.tabs.lumpSum') }}</h4>
          </template>
          <section class="pt-4 px-2">
            <app-loading :loading="loadingLumpSum" />
            <template v-if="!loadingLumpSum">
              <sme-alert v-if="errorPaySchedule" :level="ALERT_LEVELS.DANGER">{{ errorPaySchedule }}</sme-alert>
              <employee-lump-sum-pay v-else :employee="employee" :future-pay-periods="futurePayPeriods" />
            </template>
          </section>
        </b-tab>
        <b-tab v-if="showLastDayPay" :id="TAB_NAMES.last_day_pay">
          <template #title>
            <h4>{{ $t('employee.tabs.lastDayPay') }}</h4>
          </template>
          <EmployeeLastDayPayV3
            v-if="isEnableLastDayPayV3"
            class="pt-4 pb-2 px-2"
            :employee="employee"
            :balance="balance"
            :currency="state.company.default_currency"
            @finalPayProcessed="retrieveAndUpdatePageData"
          />
          <EmployeeLastDayPay
            v-else
            class="pt-4 pb-2 px-2"
            :employee="employee"
            :balance="balance"
            :currency="state.company.default_currency"
            @finalPayProcessed="retrieveAndUpdatePageData"
          />
        </b-tab>
        <b-tab v-if="employee.other_entities && employee.other_entities.length > 0" :id="TAB_NAMES.assignments">
          <template #title>
            <h4>{{ $t('employee.tabs.otherRecords') }}</h4>
          </template>
          <EmployeeAssignments
            :employee="employee"
            :payschedules="paySchedules"
            :otherAssignments="employee.other_entities"
            class="pt-4 px-2"
          />
        </b-tab>
      </navigable-tabs>
    </template>
  </app-page>
</template>

<script>
import ApiClient from '@/ApiClient';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import NavigableTabs from '@/components/NavigableTabs.vue';
import PageMetrics from '@/components/PageMetrics.vue';
import SmeAlert, { ALERT_LEVELS } from '@/components/atoms/SmeAlert.vue';
import SmeBadge from '@/components/atoms/SmeBadge.vue';
import SmeInfoBox from '@/components/atoms/SmeInfoBox.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import { employeeBadges } from '@/mixins/EmployeeBadges.js';
import { employeeHandle } from '@/mixins/EmployeeHandle.js';
import EmployeeActions from '@/pages/employee/components/EmployeeActions.vue';
import EmployeeAssignments from '@/pages/employee/components/EmployeeAssignments.vue';
import EmployeeForm from '@/pages/employee/components/EmployeeForm.vue';
import EmployeeActivityLog from '@/pages/employee/components/EmployeeActivityLog.vue';
import EmployeeHistory from '@/pages/employee/components/EmployeeHistory.vue';
import EmployeePay from '@/pages/employee/components/EmployeePay.vue';
import EmployeeLastDayPay from '@/pages/employee/components/EmployeeLastDayPay.vue';
import EmployeeLastDayPayV3 from '@/pages/employee/components/EmployeeLastDayPayV3.vue';
import EmployeeLumpSumPay from '@/pages/employee/components/EmployeeLumpSumPay.vue';
import EmployeeOffCycle from '@/pages/employee/components/EmployeeOffCycle.vue';
import AbsencesList from '@/pages/absences/components/AbsencesList.vue';

import ShiftsList from '@/pages/shifts/components/ShiftsList.vue';
import NetPayShiftsList from '@/pages/shifts/components/NetPayShiftsList.vue';
import { ABSENCE_FIELD_KEYS } from '@/pages/absences/constants';

import { SHIFT_FILTER_KEYS, SHIFT_FIELD_KEYS, NET_PAY_SHIFT_FIELD_KEYS } from '@/pages/shifts/constants';
import StreamedWagesList from '@/pages/streamed-wages/StreamedWagesList.vue';
import { STREAM_FIELD_KEYS, STREAM_FILTER_KEYS } from '@/pages/streamed-wages/constants';
import { SALARY_SETTLEMENT_FIELD_KEYS, SALARY_SETTLEMENT_FILTER_KEYS } from '@/pages/salary-settlement/constants';
import IntegrationsState from '@/state/IntegrationsState';
import State from '@/state/State';
import useEmployeesReport from '@/state/composables/useEmployeesReport';
import { toCurrency } from '@/utils';
import { EMPLOYEE_PAY_TYPE, EMPLOYEE_STATUS_LABEL_KEYS, updateEmployeeAndBanking } from '@/utils/Employee';
import moment from 'moment';

const TAB_KEYS = {
  employee: 'employee',
  streams: 'streams',
  salary_settlement: 'salary_settlement',
  shifts: 'shifts',
  pay: 'pay',
  enrollments: 'enrollments',
  activity_log: 'activity_log',
  absences: 'absences',
  assignments: 'assignments',
  last_day_pay: 'last_day_pay',
  lump_sum: 'lump_sum',
  usa_off_cycle_payments: 'usa_off_cycle_payments',
};

export default {
  name: 'Employee',
  mixins: [employeeBadges, employeeHandle],
  components: {
    AbsencesList,
    AppLoading,
    AppPage,
    EmployeeActions,
    EmployeeActivityLog,
    EmployeeForm,
    EmployeeHistory,
    EmployeeLumpSumPay,
    EmployeeOffCycle,
    EmployeePay,
    EmployeeAssignments,
    EmployeeLastDayPay,
    EmployeeLastDayPayV3,
    NavigableTabs,
    PageMetrics,
    ShiftsList,
    NetPayShiftsList,
    SmeAlert,
    SmeBadge,
    SmeInfoBox,
    StreamedWagesList,
  },
  data() {
    const shiftFieldKeys = [
      SHIFT_FIELD_KEYS.WORKED_ON,
      SHIFT_FIELD_KEYS.HOURS,
      SHIFT_FIELD_KEYS.WAGES,
      SHIFT_FIELD_KEYS.TIPS,
      SHIFT_FIELD_KEYS.WAGES_PER_HOUR,
      SHIFT_FIELD_KEYS.REMOVE_SHIFT,
    ];

    return {
      ALERT_LEVELS,
      EMPLOYEE_STATUS_LABEL_KEYS,
      SHIFT_FILTER_KEYS,
      TAB_NAMES: TAB_KEYS,
      employee: undefined,
      employeeId: undefined,
      balance: undefined,
      paySchedules: undefined,
      history: undefined,
      activity: undefined,
      optout: undefined,
      salaries: undefined,
      salaryHistory: undefined,
      futurePayPeriods: undefined,
      saving: false,
      noRecentShifts: false,
      notYetPaid: false,
      showEmployeeFlag: false,
      loadingEmployee: true,
      loadingEnrollments: true,
      loadingActivityLog: true,
      loadingLumpSum: true,
      loadingPay: true,
      loadingUSAOffCyclePayments: true,
      initialEnrollments: true,
      initialActivityLog: true,
      initialPay: true,
      initialUSAOffCyclePayments: true,
      errorEmployee: undefined,
      errorEnrollments: undefined,
      errorActivityLog: undefined,
      errorPay: undefined,
      errorPaySchedule: undefined,
      errorUSAOffCyclePayments: undefined,
      state: State.state,
      integrationsState: IntegrationsState.state,
      employeeOffCyclePayments: [],
      shiftFieldKeys,
      shiftFieldKeysHiddenRate: shiftFieldKeys.filter(key => key !== SHIFT_FIELD_KEYS.WAGES_PER_HOUR),
      shiftFieldKeysHiddenHoursAndRate: shiftFieldKeys.filter(
        key => ![SHIFT_FIELD_KEYS.HOURS, SHIFT_FIELD_KEYS.WAGES_PER_HOUR].includes(key),
      ),
      netPayShiftFieldKeys: [
        NET_PAY_SHIFT_FIELD_KEYS.WORKED_ON,
        NET_PAY_SHIFT_FIELD_KEYS.WAGES,
        NET_PAY_SHIFT_FIELD_KEYS.NET_TOTAL,
        NET_PAY_SHIFT_FIELD_KEYS.GROSS_TOTAL,
        NET_PAY_SHIFT_FIELD_KEYS.SHIFT_TYPE,
      ],
      shiftFilterKeys: [
        SHIFT_FILTER_KEYS.PAY_SCHEDULE_DATE,
        SHIFT_FILTER_KEYS.START_DATE,
        SHIFT_FILTER_KEYS.END_DATE,
        SHIFT_FILTER_KEYS.SOURCE,
      ],
      absenceFieldKeys: [
        ABSENCE_FIELD_KEYS.STARTED_AT,
        ABSENCE_FIELD_KEYS.ENDED_AT,
        ABSENCE_FIELD_KEYS.DURATION,
        ABSENCE_FIELD_KEYS.IS_PAID,
        ABSENCE_FIELD_KEYS.TYPE,
        ABSENCE_FIELD_KEYS.STATUS,
        ABSENCE_FIELD_KEYS.REFERENCE,
      ],

      streamFieldKeys: this.isEnableShowPayschedulesOnStreamedWagesPage
        ? [
            STREAM_FIELD_KEYS.NET_AMOUNT,
            STREAM_FIELD_KEYS.TYPE,
            STREAM_FIELD_KEYS.CREATED_AT,
            STREAM_FIELD_KEYS.PAY_SCHEDULE_NAME,
          ]
        : [STREAM_FIELD_KEYS.NET_AMOUNT, STREAM_FIELD_KEYS.TYPE, STREAM_FIELD_KEYS.CREATED_AT],
      streamFilterKeys: [STREAM_FILTER_KEYS.STARTS_ON, STREAM_FILTER_KEYS.ENDS_ON, STREAM_FILTER_KEYS.PAY_SCHEDULE],
      salarySettlementFieldKeys: [
        SALARY_SETTLEMENT_FIELD_KEYS.SALARY_COMPLETED_AT,
        SALARY_SETTLEMENT_FIELD_KEYS.SALARY_AMOUNT,
        SALARY_SETTLEMENT_FIELD_KEYS.AMOUNT_SETTLED,
        SALARY_SETTLEMENT_FIELD_KEYS.TOTAL_TRANSFERRED,
      ],
      salarySettlementFilterKeys: [SALARY_SETTLEMENT_FILTER_KEYS.STARTS_ON, SALARY_SETTLEMENT_FILTER_KEYS.ENDS_ON],
    };
  },
  computed: {
    employeeMetrics() {
      return [
        {
          value: 0,
          // gross_earned includes tips, so label it explicitly for tips companies to
          // disambiguate from the table's wages-only Earned Wages column.
          title: this.hasTips
            ? this.$t('employee.metrics.earnedWagesWithTips')
            : this.$t('employee.metrics.earnedWages'),
          balanceProp: 'gross_earned',
        },
        {
          value: 0,
          title: this.$t('employee.metrics.transferredWagesCurrent'),
          balanceProp: 'transferred',
        },
        {
          value: 0,
          title: this.$t('employee.metrics.transferredWagesPrevious'),
          balanceProp: 'total_debt',
        },
        {
          value: 0,
          title: this.$t('employee.metrics.availableToTransfer'),
          balanceProp: 'available_to_transfer',
        },
      ];
    },
    hasTips() {
      return this.state.company?.properties?.tips_transfer_percent != null;
    },
    hideEarnings() {
      return this.state.company.properties.hide_new_earnings && this.employee?.current_state === 'NEW';
    },
    hideSalary() {
      return (this.state.company.properties.managers_cannot_view_salary && this.state.claims.m) || this.hideSalaryView;
    },
    showUSAOffCyclePayments() {
      return this.offCyclePaymentsVersion.includes('v2');
    },
    shiftsListFieldKeys() {
      return this.hideShiftsHoursAndRate
        ? this.shiftFieldKeysHiddenHoursAndRate
        : this.hideShiftsRate
        ? this.shiftFieldKeysHiddenRate
        : this.shiftFieldKeys;
    },
    showLumpSumTab() {
      return (
        this.isEnableLumpSumPayPage && this.employee.salary_properties.salary_or_hourly === EMPLOYEE_PAY_TYPE.SALARY
      );
    },
    showLastDayPay() {
      // The Last Day Pay tab shows final-paycheck salary amounts (final_employee_salary,
      // final_net_pay_amount), so hide it when salary is hidden for this portal user.
      return this.isEnableLastDayPay && this.offCyclePaymentsVersion === 'off' && !this.hideSalary;
    },
    employeePayScheduleName() {
      if (this.paySchedules === undefined) {
        return '';
      }
      return (
        this.paySchedules.find(paySchedule => paySchedule.pay_schedule_id === this.employee.pay_schedule_id)?.name ||
        this.$t('employee.noPayScheduleFallback')
      );
    },
    extraParams() {
      return { employeeId: this.$route.params.employeeId };
    },
    flagContent() {
      let content = '';
      if (this.noRecentShifts) {
        content = this.$t('employee.flags.noRecentShifts');
      } else if (this.notYetPaid) {
        if (this.companyCanAutoEnroll) {
          content = this.$t('employee.flags.autoEnrollBanking');
        } else {
          content = this.$t('employee.flags.notYetPaid');
        }
      }
      return content;
    },
    flagLevel() {
      if (!this.noRecentShifts) {
        if (this.companyCanAutoEnroll) {
          return ALERT_LEVELS.INFO;
        }
      }
      return ALERT_LEVELS.WARNING;
    },
  },
  setup() {
    const { getEmployeesReport, isNeverPaidEmployee } = useEmployeesReport();
    const {
      isEnableStreamedWagesPage,
      isEnableShowPayschedulesOnStreamedWagesPage,
      displayNetPayShifts,
      hideShiftsHoursAndRate,
      hideShiftsRate,
      hideStreamAccountRelatedItems,
      isEnableLumpSumPayPage,
      isEnableLastDayPay,
      isEnableLastDayPayV3,
      offCyclePaymentsVersion,
      hideSalaryView,
    } = useFeatureFlags();

    return {
      getEmployeesReport,
      isNeverPaidEmployee,
      isEnableStreamedWagesPage,
      isEnableShowPayschedulesOnStreamedWagesPage,
      isEnableLumpSumPayPage,
      isEnableLastDayPay,
      isEnableLastDayPayV3,
      displayNetPayShifts,
      hideShiftsHoursAndRate,
      hideShiftsRate,
      hideStreamAccountRelatedItems,
      offCyclePaymentsVersion,
      hideSalaryView,
    };
  },
  async beforeMount() {
    this.employeeId = this.$route.params.employeeId;

    try {
      const [rawEmployee, balance, paySchedules, fieldPermissions] = await Promise.all([
        ApiClient.getPaginatedEmployees({ employee_id: this.employeeId }),
        ApiClient.getEmployeeBalance(this.employeeId),
        ApiClient.getPaySchedules(),
        ApiClient.getCompanyFieldPermissions(this.state.company.company_id),
      ]);

      const employee = rawEmployee.data[0];

      this.employee = this.setEmployeeDefaults(employee);
      this.balance = balance;
      this.paySchedules = paySchedules.data;
      this.fieldPermissions = fieldPermissions;

      for (const key in this.balance) {
        const [filteredEmployeeMetric] = this.employeeMetrics.filter(item => item.balanceProp === key);
        if (filteredEmployeeMetric) {
          filteredEmployeeMetric.value = toCurrency(this.balance[key], State.state.company.default_currency);
        }
      }
    } catch (error) {
      this.errorEmployee = error?.message || this.$t('employee.errors.loadEmployee');
    } finally {
      this.loadingEmployee = false;
    }

    this.getFlags();
  },
  methods: {
    async retrieveAndUpdatePageData() {
      this.loadingEmployee = true;
      Promise.all([
        ApiClient.getEmployee(this.employeeId),
        ApiClient.getEmployeeBalance(this.employeeId),
        ApiClient.getPaySchedules(),
        ApiClient.getCompanyFieldPermissions(this.state.company.company_id),
        ApiClient.getDivisions(this.employeeId),
        ApiClient.getEmployeeHistory(this.employeeId),
        ApiClient.getEmployeeEnrollable(this.employeeId),
        // /salaries/summary is gated for hidden-salary portal users (403/404); skip it.
        this.hideSalary ? Promise.resolve(undefined) : ApiClient.getEmployeeSalaryStatements(this.employeeId),
      ]).then(
        ([employee, balance, paySchedules, fieldPermissions, divisions, history, enrollable, salaries]) => {
          this.employee = this.setEmployeeDefaults(employee);
          this.balance = balance;
          this.paySchedules = paySchedules.data;
          this.fieldPermissions = fieldPermissions;
          this.divisions = divisions.data;
          this.history = history;
          this.enrollable = enrollable;
          this.salaries = salaries;
          this.loadingEmployee = false;
        },
        error => {
          this.error = error.message;
          this.loadingEmployee = false;
        },
      );
    },
    async getPay() {
      if (!this.initialPay) {
        return;
      }

      // Salary endpoints are gated for hidden-salary portal users (403/404); don't call them.
      if (this.hideSalary) {
        this.loadingPay = false;
        this.initialPay = false;
        return;
      }

      try {
        const [salaries, companyOffCyclePaymentHistory] = await Promise.all([
          ApiClient.getEmployeeSalaryStatements(this.employeeId),
          ApiClient.smeGetExpenseHistory(this.state.company.company_id),
        ]);

        const employeeOffCyclePayments = [];
        for (const paymentRow of companyOffCyclePaymentHistory.data) {
          const { employee_payments } = paymentRow;
          if (Array.isArray(employee_payments)) {
            for (const payment of employee_payments) {
              if (payment.employee_id === this.employeeId) {
                employeeOffCyclePayments.push(payment);
              }
            }
          }
        }

        this.salaries = salaries;
        this.employeeOffCyclePayments = employeeOffCyclePayments;
      } catch (error) {
        this.errorPay = error?.message || this.$t('employee.errors.loadPay');
      } finally {
        this.loadingPay = false;
        this.initialPay = false;
      }
    },
    async getUSAOffCyclePayments() {
      if (!this.initialUSAOffCyclePayments) {
        return;
      }
      try {
        const employeeOffCyclePayments = await ApiClient.getEmployeeOffCyclePayments(this.employeeId);

        const mappedTransferData = employeeOffCyclePayments?.data
          ?.filter(batch => !batch.properties?.hidden)
          .map(batch => ({
            ...batch,
            type: batch.properties?.upload_type || 'bulk',
            id: batch.off_cycle_payment_group_id,
            batch: `Batch #${batch.batch_number}`,
            employee_name: batch.full_name,
            amount: batch.amount,
          }))
          .sort((a, b) => b.batch_number - a.batch_number);

        this.employeeOffCyclePayments = mappedTransferData;
      } catch (error) {
        this.errorUSAOffCyclePayments = error?.message || this.$t('employee.errors.loadOffCyclePayments');
      } finally {
        this.loadingUSAOffCyclePayments = false;
        this.initialUSAOffCyclePayments = false;
      }
    },
    async getEnrollments() {
      if (!this.initialEnrollments) {
        return;
      }

      try {
        this.history = await ApiClient.getEmployeeHistory(this.employeeId);
      } catch (error) {
        this.errorEnrollments = error?.message || this.$t('employee.errors.loadEnrollments');
      } finally {
        this.loadingEnrollments = false;
        this.initialEnrollments = false;
      }
    },
    async getActivityLog() {
      if (!this.initialActivityLog) {
        return;
      }

      try {
        // Skip salary history when hidden: the result is discarded in the template
        // (salary-history is passed as []) and the endpoint may be gated for these users.
        const [activity, salaryHistory, optout] = await Promise.all([
          ApiClient.getEmployeeActivity(this.employeeId),
          this.hideSalary ? Promise.resolve({ data: [] }) : ApiClient.smeGetSalaryHistory(this.employeeId),
          ApiClient.getEmployeeOptout(this.employeeId),
        ]);

        this.activity = activity.activity;
        this.salaryHistory = salaryHistory.data;
        this.optout = optout.data;
      } catch (error) {
        this.errorActivityLog = error?.message || this.$t('employee.errors.loadActivityLog');
      } finally {
        this.loadingActivityLog = false;
        this.initialActivityLog = false;
      }
    },
    async getLumpSumData() {
      try {
        const payPeriods = await ApiClient.getPayScheduleDates(this.employee.pay_schedule_id);
        const filteredDates = payPeriods.filter(
          payPeriod => moment(payPeriod.pay_period_start).isSameOrAfter(moment(), 'day') || !payPeriod.paid_on,
        );
        this.futurePayPeriods = filteredDates;
      } catch (error) {
        this.errorPaySchedule = error?.message || this.$t('employee.errors.loadPaySchedule');
      } finally {
        this.loadingLumpSum = false;
      }
    },
    async getFlags() {
      try {
        // deprecating, this is too slow
        const [, noShiftEmployees] = await Promise.all([
          this.getEmployeesReport(null, null, { neverPaid: true }),
          ApiClient.smeGetEmployeesWithoutShifts(30),
        ]);

        if (
          noShiftEmployees?.data.employees?.some(employee => employee.employee_id === this.employeeId) &&
          this.employee.salary_properties.salary_or_hourly === EMPLOYEE_PAY_TYPE.HOURLY
        ) {
          this.noRecentShifts = true;
        }

        this.notYetPaid = this.isNeverPaidEmployee(this.employeeId);

        if (
          (this.noRecentShifts || (this.notYetPaid && !this.hideStreamAccountRelatedItems)) &&
          this.isEmployeeEnrolled(this.employee)
        ) {
          this.showEmployeeFlag = true;
        }
      } catch (error) {
        console.error(error);
      }
    },
    async onSave(params) {
      const failureCallback = params.failureCallback;
      const salaryToHourlyDate = params.salaryToHourlyDate;
      const salaryToHourlyChange = params.salaryToHourlyChange;
      const newSalaryProps = {
        salary_or_hourly: EMPLOYEE_PAY_TYPE.HOURLY,
        salary_period: 'YEAR',
        default_wagerate: undefined,
      };
      this.saving = true;
      this.ensureNoEmptyStringsInEmployee();
      try {
        if (salaryToHourlyChange) {
          ApiClient.switchToHourly(this.employee.employee_id, newSalaryProps, salaryToHourlyDate);
        }
        await updateEmployeeAndBanking(this.employee);
        this.saving = false;
        this.$appToast(this.$t('employee.save.successToastBody', { name: this.employee.full_name }), {
          title: this.$t('employee.save.successToastTitle'),
          variant: 'success',
        });
      } catch (error) {
        this.employee = failureCallback();
        this.$appToast(this.$t('employee.save.errorToastBody', { message: error ? error.message : 'NULL' }), {
          title: this.$t('employee.save.errorToastTitle'),
          variant: 'warning',
        });
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.employee-status-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.employee-page {
  :deep(.page-sub-header__container) {
    justify-content: flex-start;
  }

  :deep(.page-sub-header__titles) {
    flex: 0 1 auto;
    min-width: auto;
  }

  :deep(.page-sub-header__nav) {
    justify-content: flex-start;
  }
}
</style>
